import { REQUIREMENT_TYPES_NO_EXPIRATION_STRATEGY } from "@wingspanhq/payments/dist/interfaces";
import {
  RequirementType,
  SharedFileRequestContextType
} from "@wingspanhq/wingspan-api-sdk";

import { REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS } from "../../../Requirements/modules/EmployeeTaxForms/constants";
import { IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS } from "../../../Requirements/modules/IdentityVerification/instructions";
import { CreateRequirementDefinitionSharedFileMimeType } from "../../../Requirements/modules/SharedFile/types";

import businessCardImg from "./assets/business-card.svg";
import driversLicenseImg from "./assets/drivers-license.svg";
import headshotImg from "./assets/headshot.svg";
import idVerificationImg from "./assets/id-verification.svg";
import generalLiabilityImg from "./assets/insurance-verification.svg";
import resumeImg from "./assets/resume.svg";
import sharedFileImg from "./assets/shared-file.svg";
import signatureImg from "./assets/signature.svg";
import { GENERAL_LIABILITY_FORM } from "./components/forms/GeneralLiability";
import { PROFESSIONAL_LIABILITY_FORM } from "./components/forms/ProfessionalLiability";
import { STANDARD_BACKGROUND_CHECK_FORM } from "./components/forms/StandardBackgroundCheck";
import { STANDARD_ESIGNATURE_FORM } from "./components/forms/StandardESignature";
import { STANDARD_IDENTITY_VERIFICATION_FORM } from "./components/forms/StandardIdentityVerification";
import { STANDARD_SHARED_FILE_FORM } from "./components/forms/StandardSharedFile";
import { WORK_INJURY_FORM } from "./components/forms/WorkInjury";
import { getSmartSharedFileForm } from "./components/forms/getSmartSharedFileForm";
import {
  BACKGROUND_CHECK,
  BUSINESS_CARD,
  DRIVERS_LICENSE,
  ESignature,
  FILE_UPLOAD,
  GENERAL_LIABILITY,
  HEADSHOT,
  ID_VERIFICATION,
  PROFESSIONAL_LIABILITY,
  RESUME,
  WORK_INJURY
} from "./constants";
import { RequirementDefinitionTemplate } from "./types";

const templatesSource: RequirementDefinitionTemplate[] = [
  // Standard templates
  {
    form: STANDARD_ESIGNATURE_FORM,
    icon: "edit-line",
    image: signatureImg,
    isCustomName: true,
    isRequirementWithErrorStates: false,
    key: "signature",
    modal: {
      cost: ESignature.cost,
      description: ESignature.description,
      exampleUseCases: ESignature.exampleUseCases
    },
    name: "E-signature",
    requirementType: RequirementType.SIGNATURE,
    tier: "Standard"
  },
  {
    contextType: SharedFileRequestContextType.NONE,
    form: STANDARD_SHARED_FILE_FORM,
    icon: "upload",
    image: sharedFileImg,
    isCustomName: true,
    isRequirementWithErrorStates: false,
    key: "shared-file",
    modal: {
      cost: FILE_UPLOAD.cost,
      description: FILE_UPLOAD.description,
      exampleUseCases: FILE_UPLOAD.exampleUseCases
    },
    name: "File upload",
    requirementType: RequirementType.DOCUMENT_UPLOAD,
    tier: "Standard"
  },

  // Smart (Billable) document verification templates (managed off-platform)
  {
    contextType: SharedFileRequestContextType.DRIVERS_LICENSE,
    costPerSubmission: 1.99,
    form: getSmartSharedFileForm({
      [CreateRequirementDefinitionSharedFileMimeType.Document]: true,
      [CreateRequirementDefinitionSharedFileMimeType.Image]: true
    }),
    helperText: "Document verification",
    icon: "id",
    image: driversLicenseImg,
    instructions: DRIVERS_LICENSE.instructions,
    isBillable: true,
    isRequirementWithErrorStates: true,
    key: "drivers-license",
    modal: {
      cost: DRIVERS_LICENSE.cost,
      description: DRIVERS_LICENSE.description,
      disclaimer: DRIVERS_LICENSE.disclaimer
    },

    name: "Driver’s license",
    payerInfo: {
      cost: DRIVERS_LICENSE.cost,
      description: DRIVERS_LICENSE.description,
      disclaimer: DRIVERS_LICENSE.disclaimer
    },
    requirementType: RequirementType.DOCUMENT_UPLOAD,
    singleInstance: true,
    tier: "Smart"
  },
  {
    contextType: SharedFileRequestContextType.HEADSHOT,
    costPerSubmission: 1.99,
    form: getSmartSharedFileForm({
      [CreateRequirementDefinitionSharedFileMimeType.Image]: true
    }),
    helperText: "Document verification",
    icon: "user-fill",
    image: headshotImg,
    instructions: HEADSHOT.instructions,
    isBillable: true,
    isRequirementWithErrorStates: true,
    key: "headshot",
    modal: {
      cost: HEADSHOT.cost,
      description: HEADSHOT.description,
      exampleUseCases: HEADSHOT.exampleUseCases
    },
    name: "Headshot (photo)",
    payerInfo: {
      cost: HEADSHOT.cost,
      description: HEADSHOT.description,
      exampleUseCases: HEADSHOT.exampleUseCases
    },
    requirementType: RequirementType.DOCUMENT_UPLOAD,
    singleInstance: true,
    tier: "Smart"
  },
  {
    contextType: SharedFileRequestContextType.RESUME,
    costPerSubmission: 1.99,
    form: getSmartSharedFileForm({
      [CreateRequirementDefinitionSharedFileMimeType.Document]: true,
      [CreateRequirementDefinitionSharedFileMimeType.Other]: true
    }),
    helperText: "Document verification",
    icon: "file",
    image: resumeImg,
    instructions: RESUME.instructions,
    isBillable: true,
    isRequirementWithErrorStates: true,
    key: "resume",
    modal: {
      cost: RESUME.cost,
      description: RESUME.description,
      exampleUseCases: RESUME.exampleUseCases
    },
    name: "Resume",
    payerInfo: {
      cost: RESUME.cost,
      description: RESUME.description,
      exampleUseCases: RESUME.exampleUseCases
    },
    requirementType: RequirementType.DOCUMENT_UPLOAD,
    singleInstance: true,
    tier: "Smart"
  },
  {
    contextType: SharedFileRequestContextType.BUSINESS_CARD,
    costPerSubmission: 1.99,
    form: getSmartSharedFileForm({
      [CreateRequirementDefinitionSharedFileMimeType.Image]: true,
      [CreateRequirementDefinitionSharedFileMimeType.Document]: true
    }),
    helperText: "Document verification",
    icon: "details",
    image: businessCardImg,
    instructions: BUSINESS_CARD.instructions,
    isBillable: true,
    isRequirementWithErrorStates: true,
    key: "business-card",
    modal: {
      cost: BUSINESS_CARD.cost,
      description: BUSINESS_CARD.description,
      exampleUseCases: BUSINESS_CARD.exampleUseCases
    },
    name: "Business card",
    payerInfo: {
      cost: BUSINESS_CARD.cost,
      description: BUSINESS_CARD.description,
      exampleUseCases: BUSINESS_CARD.exampleUseCases
    },
    requirementType: RequirementType.DOCUMENT_UPLOAD,
    singleInstance: true,
    tier: "Smart"
  },

  // SmartAdvanced templates
  {
    costPerSubmission: 5,
    form: STANDARD_IDENTITY_VERIFICATION_FORM,
    helperText: "Biometrics matching",
    icon: "id",
    image: idVerificationImg,
    instructions: IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS,
    isBillable: true,
    isRequirementWithErrorStates: true,
    key: "id-verification",
    modal: {
      cost: ID_VERIFICATION.cost,
      description: ID_VERIFICATION.description,
      exampleUseCases: ID_VERIFICATION.exampleUseCases
    },
    name: "ID verification",
    payerInfo: {
      cost: ID_VERIFICATION.cost,
      description: ID_VERIFICATION.description,
      exampleUseCases: ID_VERIFICATION.exampleUseCases
    },
    requirementType: RequirementType.BIOMETRIC_IDENTITY_VERIFICATION,
    singleInstance: true,
    tier: "SmartAdvanced"
  },
  {
    costPerSubmission: 15,
    form: GENERAL_LIABILITY_FORM,
    helperText: "Insurance verification",
    icon: "shield-check-fill",
    image: generalLiabilityImg,
    isBillable: true,
    isPolicy: true,
    isRequirementWithErrorStates: true,
    isSmartAdvanced: true,
    key: "general-liability",
    modal: {
      cost: GENERAL_LIABILITY.cost,
      description: GENERAL_LIABILITY.description,
      exampleUseCases: GENERAL_LIABILITY.exampleUseCases
    },
    name: "General liability",
    policyType: ["GeneralLiability"],
    requirementType: RequirementType.INSURANCE_COVERAGE,
    singleInstance: true,
    tier: "SmartAdvanced"
  },
  {
    costPerSubmission: 15,
    form: PROFESSIONAL_LIABILITY_FORM,
    helperText: "Insurance verification",
    icon: "shield-check-fill",
    image: generalLiabilityImg,
    isBillable: true,
    isPolicy: true,
    isRequirementWithErrorStates: true,
    isSmartAdvanced: true,
    key: "professional-liability",
    modal: {
      cost: PROFESSIONAL_LIABILITY.cost,
      description: PROFESSIONAL_LIABILITY.description,
      exampleUseCases: PROFESSIONAL_LIABILITY.exampleUseCases
    },
    name: "Professional liability",
    policyType: ["ProfessionalLiability"],
    requirementType: RequirementType.INSURANCE_COVERAGE,
    singleInstance: true,
    tier: "SmartAdvanced"
  },
  {
    costPerSubmission: 15,
    form: WORK_INJURY_FORM,
    helperText: "Insurance verification",
    icon: "shield-check-fill",
    image: generalLiabilityImg,
    isBillable: true,
    isPolicy: true,
    isRequirementWithErrorStates: true,
    isSmartAdvanced: true,
    key: "work-injury-coverage",

    modal: {
      cost: WORK_INJURY.cost,
      description: WORK_INJURY.description,
      exampleUseCases: WORK_INJURY.exampleUseCases
    },
    name: "Work injury coverage",
    policyType: ["OccupationalAccident"],
    requirementType: RequirementType.INSURANCE_COVERAGE,
    singleInstance: true,
    tier: "SmartAdvanced"
  },
  /* {
    key: "auto-liability",
    name: "Auto liability",
    icon: "car",
    helperText: "Insurance verification",
    image: autoInsuranceVerificationImg,
    requirementType: RequirementType.INSURANCE_COVERAGE,
    policyType: "AutomobileLiability",
    tier: "SmartAdvanced",
    isBillable: true,
    modal: {
      description: AUTO_LIABILITY.description,
      exampleUseCases: AUTO_LIABILITY.exampleUseCases,
      cost: AUTO_LIABILITY.cost
    },
    form: AUTO_LIABILITY_FORM,
    instructions: getAutoInsuranceCardInstructions(),
    validations: Object.values(AUTO_INSURANCE_CARD_JOBS).map(job => ({
      title: job.title,
      description: job.description
    })),
    costPerSubmission: 1.99,
    isRequirementWithErrorStates: true,
    isSmartAdvanced: true
  }, */
  {
    form: STANDARD_BACKGROUND_CHECK_FORM,
    icon: "shield-check-fill",
    image: idVerificationImg,
    isBillable: true,
    isCustomName: true,
    isRequirementWithErrorStates: true,
    key: "background-check",
    modal: {
      cost: BACKGROUND_CHECK.cost,
      description: BACKGROUND_CHECK.description,
      exampleUseCases: BACKGROUND_CHECK.exampleUseCases
    },
    name: "Background check",
    requirementType: RequirementType.BACKGROUND_CHECK,
    tier: "SmartAdvanced"
  },
  {
    contextType: SharedFileRequestContextType.AUTO_INSURANCE_CARD,
    form: null,
    helperText: "Auto insurance verification",
    hidden: true,
    icon: "upload",
    isBillable: true,
    isCustomName: true,
    isRequirementWithErrorStates: true,
    key: "shared-file-coi-ai",
    modal: {
      cost: "",
      description: "",
      exampleUseCases: ""
    },
    name: "File upload",
    requirementType: RequirementType.DOCUMENT_UPLOAD,
    tier: "Smart"
  },
  {
    form: null,
    hidden: true,
    icon: "user-check",
    isBillable: false,
    isCustomName: false,
    isRequirementWithErrorStates: false,
    key: "registration",
    modal: {
      cost: "",
      description: "",
      exampleUseCases: ""
    },
    name: "Onboarding",
    requirementType: RequirementType.REGISTRATION,
    tier: "Standard"
  },
  {
    form: null,
    hidden: true,
    icon: "clipboard-check",
    isBillable: false,
    isCustomName: false,
    isRequirementWithErrorStates: false,
    key: "tax-verification",
    modal: {
      cost: "",
      description: "",
      exampleUseCases: ""
    },
    name: "Form",
    requirementType: RequirementType.TAX_VERIFICATION,
    tier: "Standard"
  },
  {
    form: null,
    hidden: true,
    icon: "bank",
    isBillable: false,
    isCustomName: false,
    isRequirementWithErrorStates: false,
    key: "payout-method",
    modal: {
      cost: "",
      description: "",
      exampleUseCases: ""
    },
    name: "Bank account",
    requirementType: RequirementType.PAYOUT_METHOD,
    tier: "Standard"
  },
  // Hidden: created via API only (no payer create-gallery flow yet); the
  // entry backs the type-cell / name lookups for existing requirements.
  {
    form: null,
    hidden: true,
    icon: "clipboard-check",
    isBillable: false,
    isCustomName: true,
    isRequirementWithErrorStates: false,
    key: "employee-tax-forms",
    modal: {
      cost: "",
      description: "",
      exampleUseCases: ""
    },
    name: "Employee tax forms",
    // Wire value the installed SDK enum predates — cast until it ships.
    requirementType:
      REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS as unknown as RequirementType,
    tier: "Standard"
  }
];

export const TEMPLATES: RequirementDefinitionTemplate[] = templatesSource.map(
  template => {
    return {
      ...template,
      // the v1 constant is a list of type *values*; compare on the string
      noExpirationForm: (
        REQUIREMENT_TYPES_NO_EXPIRATION_STRATEGY as string[]
      ).includes(template.requirementType)
    };
  }
);
